import { AlertTriangle } from "lucide-react";
import BalanceCard from "./BalanceCard";
import OpeningBalanceNote from "./OpeningBalanceNote";
import { LEAVE_TYPES } from "../../lib/leaveOptions";
import { plural } from "../../lib/vocabulary";

/**
 * The balance row — ONE implementation. Three pages carried their own
 * `["CL","SL","PL"].map(...)` grid; this is the promoted copy, two columns
 * for the two types, each tile answering accrued · availed · balance and the
 * PL tile carrying its cap warning and visible lapse counter (P5/P8).
 *
 * The values are passed through UNTOUCHED. They used to arrive here as
 * `balance[t]?.accrued ?? 0`, which turned a type the response never carried
 * into a confident "0 / 0" before BalanceCard could tell the two apart.
 * Missing stays missing all the way to the renderer that says so.
 */
export default function BalanceRow({ balance, testIdPrefix }) {
  if (!balance) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-row`}>
      {LEAVE_TYPES.map(({ value: t }) => (
        <BalanceCard
          key={t}
          type={t}
          accrued={balance[t]?.accrued}
          availed={balance[t]?.availed}
          balance={balance[t]?.balance}
          testId={`${testIdPrefix}-${t}`}
          extra={t === "PL" ? <PlNotes pl={balance.PL} prefix={testIdPrefix} /> : null}
        />
      ))}
    </div>
  );
}

function PlNotes({ pl, prefix }) {
  if (!pl) return null;
  // PL is a COMPUTED balance (P5/P8), so the lapse counter is part of the
  // answer and not a footnote. A lapse figure the response did not carry is
  // NOT zero days lapsed — an absent counter and a clean year read identically
  // otherwise, and only one of them is a fact.
  const lapsedReported = pl.lapsed_ytd !== null && pl.lapsed_ytd !== undefined;
  return (
    <>
      {/* THE OPENING COMPONENT, on the employee's own view (D-004). A number
          added to somebody's leave without a name, a date and a reason beside
          it is indistinguishable from a bug, and the person it belongs to is
          the one who would notice. */}
      <OpeningBalanceNote pl={pl} testId={`${prefix}-pl-opening`} />
      {lapsedReported && pl.lapsed_ytd > 0 && (
        <p
          className="mt-1 text-[11px] text-orange-700"
          data-testid={`${prefix}-pl-lapsed`}
        >
          {plural(pl.lapsed_ytd, "day")} lapsed this year at the {pl.cap}-day
          ceiling
        </p>
      )}
      {!lapsedReported && (
        <p
          className="mt-1 text-[11px] text-callisto-muted"
          data-testid={`${prefix}-pl-lapsed-unknown`}
        >
          Lapsed days not reported
        </p>
      )}
      {pl.warning && (
        <p
          className="mt-1 text-[11px] text-amber-800 flex items-center gap-1"
          data-testid={`${prefix}-pl-warning`}
        >
          <AlertTriangle size={11} />
          Approaching the PL ceiling — days accrued beyond {pl.cap} will lapse
        </p>
      )}
    </>
  );
}
